import { ApiRequestError, apiFetch, apiRequest } from './client'
import { isApiRecord } from './responseDecoders'
import {
  isSkillManifestItem,
  type DirectorSkillJsonValue,
  type SkillCoverView,
  type SkillCatalogItem,
  type SkillCatalogPage,
  type SkillCreateVersionRequest,
  type SkillFileUpload,
  type SkillFileView,
  type SkillManifestItem,
  type SkillManifestSource,
  type SkillInstallRequest,
  type SkillInstallationView,
  type SkillImportCompatibilityResult,
  type SkillPackageLimits,
  type SkillPublicationSubmitRequest,
  type SkillPublicationView,
  type SkillValidateRequest,
  type SkillValidationResult,
  type SkillVersionResult,
  type SkillView,
} from '../generated/directorSkillTypes'

export type DirectorSkillSource = SkillManifestSource

export interface DirectorSkillSummary {
  ref: string
  source: DirectorSkillSource
  skillId: string
  versionId: string
  version: number
  name: string
  publicSlug: string
  displayName: string
  description: string
  useWhen: string
  howToUse: string
  outputContent: string
  cover?: SkillCoverView | null
  installed?: boolean
  enabled?: boolean
  available?: boolean
  updateAvailable?: boolean
  installedRef?: string
  installedVersionId?: string
  installedVersion?: number
  publisherDisplayName?: string
  installCount?: number
}

type DirectorSkillSlugFields = {
  name: string
  public_slug?: string | null
}

function publicSlugFromApi(raw: DirectorSkillSlugFields): string {
  return raw.public_slug || raw.name
}

export function directorSkillPublicSlug(skill: Pick<DirectorSkillSummary, 'name' | 'publicSlug'>): string {
  return skill.publicSlug || skill.name
}

export function directorSkillSerializedMarker(skill: Pick<DirectorSkillSummary, 'name' | 'publicSlug'>): string {
  return `[[/${directorSkillPublicSlug(skill)}]]`
}

export function directorSkillSerializedMarkers(skill: Pick<DirectorSkillSummary, 'name' | 'publicSlug'>): string[] {
  const primary = directorSkillSerializedMarker(skill)
  const legacy = `[[/${skill.name}]]`
  return primary === legacy ? [primary] : [primary, legacy]
}

export function toDirectorSkillSummary(raw: SkillManifestItem): DirectorSkillSummary {
  return {
    ref: raw.ref,
    source: raw.source,
    skillId: raw.skill_id,
    versionId: raw.version_id,
    version: raw.version,
    name: raw.name,
    publicSlug: publicSlugFromApi(raw),
    displayName: raw.display_name,
    description: raw.description,
    useWhen: raw.use_when,
    howToUse: raw.how_to_use,
    outputContent: raw.output_content,
    cover: raw.cover,
  }
}

export function skillVersionResultToDirectorSkillSummary(raw: SkillVersionResult): DirectorSkillSummary {
  return {
    ...toDirectorSkillSummary(raw),
    enabled: raw.enabled,
    available: raw.enabled && raw.is_active,
  }
}

export function catalogItemToDirectorSkillSummary(raw: SkillCatalogItem): DirectorSkillSummary {
  return {
    ref: raw.ref,
    source: 'published',
    skillId: raw.skill_id,
    versionId: raw.version_id,
    version: raw.version,
    name: raw.name,
    publicSlug: publicSlugFromApi(raw),
    displayName: raw.display_name || raw.name,
    description: raw.description,
    useWhen: raw.use_when,
    howToUse: raw.how_to_use,
    outputContent: raw.output_content,
    cover: raw.cover,
    installed: raw.installed_version_id !== null && raw.installed_version_id !== undefined,
    installedVersionId: raw.installed_version_id ?? undefined,
    enabled: raw.installed_enabled ?? undefined,
    available: true,
    updateAvailable: raw.update_available,
    publisherDisplayName: raw.publisher_display_name,
    installCount: raw.install_count,
  }
}

export function installationToDirectorSkillSummary(raw: SkillInstallationView): DirectorSkillSummary {
  return {
    ref: raw.ref,
    source: 'published',
    skillId: raw.skill_id,
    versionId: raw.pinned_version_id,
    version: raw.version,
    name: raw.name,
    publicSlug: publicSlugFromApi(raw),
    displayName: raw.display_name || raw.name,
    description: raw.description,
    useWhen: raw.use_when,
    howToUse: raw.how_to_use,
    outputContent: raw.output_content,
    cover: raw.cover,
    installed: true,
    enabled: raw.enabled,
    available: raw.available,
    updateAvailable: raw.update_available,
    installedRef: raw.ref,
    installedVersionId: raw.pinned_version_id,
    installedVersion: raw.version,
    publisherDisplayName: raw.publisher_display_name,
  }
}

export function ownedSkillToDirectorSkillSummary(raw: SkillView): DirectorSkillSummary {
  const refParts = raw.ref.split(':')
  const fallbackName = refParts[refParts.length - 1] || 'skill'
  const name = raw.name || fallbackName
  return {
    ref: raw.ref,
    source: raw.source,
    skillId: raw.skill_id,
    versionId: raw.version_id ?? '',
    version: raw.version ?? 0,
    name,
    publicSlug: raw.public_slug || name,
    displayName: raw.display_name || name,
    description: raw.description ?? '',
    useWhen: raw.use_when ?? '',
    howToUse: raw.how_to_use ?? '',
    outputContent: raw.output_content ?? '',
    cover: raw.cover,
    enabled: raw.enabled,
    available: raw.enabled && raw.is_active && raw.version_id !== undefined,
  }
}

export function resolveRuntimeDirectorSkill(skill: DirectorSkillSummary): DirectorSkillSummary | null {
  if (skill.enabled === false || skill.available === false) return null
  if (skill.source !== 'published') return skill
  if (!skill.installed || skill.enabled !== true || skill.available !== true) return null
  if (!skill.installedRef || !skill.installedVersionId || skill.installedVersion === undefined) return null
  return {
    ...skill,
    ref: skill.installedRef,
    versionId: skill.installedVersionId,
    version: skill.installedVersion,
  }
}

function parseSkillManifestItem(raw: DirectorSkillJsonValue): SkillManifestItem {
  if (!isSkillManifestItem(raw)) {
    throw new Error('Director skill API returned invalid metadata.')
  }
  return raw
}

export async function listAvailableDirectorSkills(): Promise<DirectorSkillSummary[]> {
  const raw = await apiRequest<DirectorSkillJsonValue>('/user/director-skills/available')
  if (!Array.isArray(raw)) throw new Error('Director skill API returned an invalid list.')
  return raw.map(parseSkillManifestItem).map(toDirectorSkillSummary)
}

export async function listOwnedDirectorSkills(): Promise<DirectorSkillSummary[]> {
  const skills = await apiRequest<SkillView[]>('/user/director-skills')
  return skills.map(ownedSkillToDirectorSkillSummary)
}

export async function listDirectorSkillCatalog(): Promise<DirectorSkillSummary[]> {
  const items: DirectorSkillSummary[] = []
  const seenOffsets = new Set<number>()
  let nextOffset: number | null = 0
  while (nextOffset !== null) {
    const offset: number = nextOffset
    if (seenOffsets.has(offset)) throw new Error('Director skill catalog returned a repeated pagination offset.')
    seenOffsets.add(offset)
    const page: SkillCatalogPage = await apiRequest<SkillCatalogPage>(`/user/director-skills/catalog?limit=100&offset=${offset}`)
    items.push(...page.items.map(catalogItemToDirectorSkillSummary))
    if (page.next_offset === null || page.next_offset === undefined) {
      nextOffset = null
      continue
    }
    if (!Number.isInteger(page.next_offset) || page.next_offset <= offset) {
      throw new Error('Director skill catalog returned an invalid pagination offset.')
    }
    nextOffset = page.next_offset
  }
  return items
}

export async function listInstalledDirectorSkills(): Promise<DirectorSkillSummary[]> {
  const installations = await apiRequest<SkillInstallationView[]>('/user/director-skills/installations?limit=100&offset=0')
  return installations.map(installationToDirectorSkillSummary)
}

export async function installDirectorSkill(skillId: string, request: SkillInstallRequest): Promise<DirectorSkillSummary> {
  const installation = await apiRequest<SkillInstallationView>(`/user/director-skills/catalog/${skillId}/install`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
  return installationToDirectorSkillSummary(installation)
}

export async function updateDirectorSkillInstallation(skillId: string): Promise<DirectorSkillSummary> {
  const installation = await apiRequest<SkillInstallationView>(`/user/director-skills/installations/${skillId}/update`, {
    method: 'POST',
  })
  return installationToDirectorSkillSummary(installation)
}

export async function uninstallDirectorSkill(skillId: string): Promise<void> {
  await apiRequest<void>(`/user/director-skills/installations/${skillId}`, {
    method: 'DELETE',
  })
}

export async function readOwnedDirectorSkill(skillId: string): Promise<SkillView> {
  return apiRequest<SkillView>(`/user/director-skills/${skillId}`)
}

function requiredLimit(raw: Record<string, DirectorSkillJsonValue>, field: string, allowZero = false): number {
  const value = raw[field]
  if (typeof value !== 'number' || !Number.isInteger(value) || (allowZero ? value < 0 : value <= 0)) {
    throw new Error(`Director skill package limits returned invalid ${field}.`)
  }
  return value
}

export async function readDirectorSkillPackageLimits(): Promise<SkillPackageLimits> {
  const raw = await apiRequest<DirectorSkillJsonValue>('/user/director-skills/limits')
  if (!isApiRecord(raw)) throw new Error('Director skill package limits returned an invalid response.')
  if (raw.document_filename !== 'SKILL.md' || raw.reference_media_type !== 'text/markdown' || raw.reference_suffix !== '.md') {
    throw new Error('Director skill package limits returned an unsupported file contract.')
  }
  return {
    max_user_file_count: requiredLimit(raw, 'max_user_file_count'),
    max_upload_file_count: requiredLimit(raw, 'max_upload_file_count', true),
    max_user_payload_bytes: requiredLimit(raw, 'max_user_payload_bytes'),
    max_document_bytes: requiredLimit(raw, 'max_document_bytes'),
    max_reference_file_bytes: requiredLimit(raw, 'max_reference_file_bytes'),
    max_reference_result_bytes: requiredLimit(raw, 'max_reference_result_bytes'),
    max_reference_request_bytes: requiredLimit(raw, 'max_reference_request_bytes'),
    max_cover_image_bytes: requiredLimit(raw, 'max_cover_image_bytes'),
    max_cover_video_bytes: requiredLimit(raw, 'max_cover_video_bytes'),
    document_filename: raw.document_filename,
    reference_media_type: raw.reference_media_type,
    reference_suffix: raw.reference_suffix,
  }
}

export async function validateDirectorSkillDocument(request: SkillValidateRequest): Promise<SkillValidationResult> {
  return apiRequest<SkillValidationResult>('/user/director-skills/validate', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export interface DirectorSkillStructuredError {
  code: string
  field?: string
  limit?: number
  actual?: number
}

export function readDirectorSkillStructuredError(error: Error): DirectorSkillStructuredError | null {
  if (!(error instanceof ApiRequestError) || (error.status !== 413 && error.status !== 422) || !isApiRecord(error.errorData)) return null
  const detail = error.errorData.detail
  if (!isApiRecord(detail) || typeof detail.error !== 'string') return null
  return {
    code: detail.error,
    ...(typeof detail.field === 'string' ? { field: detail.field } : {}),
    ...(typeof detail.limit === 'number' ? { limit: detail.limit } : {}),
    ...(typeof detail.actual === 'number' ? { actual: detail.actual } : {}),
  }
}

export async function createOwnedDirectorSkillVersion(skillId: string, request: SkillCreateVersionRequest): Promise<SkillVersionResult> {
  return apiRequest<SkillVersionResult>(`/user/director-skills/${skillId}/versions`, { method: 'POST', body: JSON.stringify(request) })
}

export async function publishOwnedDirectorSkill(skillId: string, request: SkillPublicationSubmitRequest): Promise<SkillPublicationView> {
  return apiRequest<SkillPublicationView>(`/user/director-skills/${skillId}/publication-submissions`, { method: 'POST', body: JSON.stringify(request) })
}

export async function deleteOwnedDirectorSkill(skillId: string): Promise<void> {
  await apiRequest<void>(`/user/director-skills/${skillId}`, {
    method: 'DELETE',
  })
}

export async function readDirectorSkillFile(file: SkillFileView): Promise<Blob> {
  if (!file.content_url) throw new Error(`Skill file ${file.path} is not available to this viewer.`)
  return readDirectorSkillAsset(file.content_url, file.path)
}

export async function readDirectorSkillAsset(path: string, label = 'asset'): Promise<Blob> {
  const response = await apiFetch(path)
  if (!response.ok) {
    throw new Error(`Could not load skill file ${label}: ${response.status} ${response.statusText}`)
  }
  return response.blob()
}

export async function skillFileToUpload(file: File, path = file.name): Promise<SkillFileUpload> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  const chunkSize = 0x8000
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
  }
  return {
    path,
    media_type: file.type || 'application/octet-stream',
    content_base64: btoa(binary),
  }
}

export async function blobToSkillFileUpload(blob: Blob, path: string, mediaType: string): Promise<SkillFileUpload> {
  const pathParts = path.split('/')
  const file = new File([blob], pathParts[pathParts.length - 1] || path, {
    type: mediaType,
  })
  return skillFileToUpload(file, path)
}

export async function importDirectorSkillMarkdown(
  file: File,
  options: { onRewriteRequired?: () => void } = {},
): Promise<DirectorSkillSummary> {
  if (!file.name.toLocaleLowerCase().endsWith('.md')) {
    throw new Error('Choose a Markdown (.md) file.')
  }
  if (file.size <= 0 || file.size > 65_536) {
    throw new Error('Markdown skill files must be between 1 byte and 64 KB.')
  }
  const bytes = new Uint8Array(await file.arrayBuffer())
  let content: string
  try {
    // Keep a UTF-8 BOM as U+FEFF so re-encoding the JSON string reproduces
    // every source byte; reject invalid encodings instead of storing mojibake.
    content = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes)
  } catch {
    throw new Error('Markdown skill files must use valid UTF-8 encoding.')
  }
  const encoded = new TextEncoder().encode(content)
  if (encoded.length !== bytes.length || encoded.some((byte, index) => byte !== bytes[index])) {
    throw new Error('Markdown skill files must use canonical UTF-8 encoding.')
  }
  const compatibility = await apiRequest<SkillImportCompatibilityResult>('/user/director-skills/import/compatibility', {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, content_md: content }),
  })
  if (!compatibility.compatible) options.onRewriteRequired?.()
  const raw = await apiRequest<DirectorSkillJsonValue>('/user/director-skills/import', {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, content_md: content }),
  })
  return toDirectorSkillSummary(parseSkillManifestItem(raw))
}
